import { Component, computed, inject, input, OnInit, PLATFORM_ID } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCalendar, faRotateLeft, faSearch, faShoppingBag, faTicket } from "@fortawesome/free-solid-svg-icons";
import { ButtonModule } from "primeng/button";
import { InputGroupModule } from "primeng/inputgroup";
import { ThemeService } from "../../../services/theme.service";
import { MessageModule } from "primeng/message";
import { best, ratio } from 'wcag-color';
import { ColorPickerModule } from 'primeng/colorpicker';
import { isPlatformBrowser } from "@angular/common";
import { InputTextModule } from 'primeng/inputtext';
import { environment } from "../../../../environments/environment";
import { colord } from "colord";

const AUCTION_MANAGER_THEME_PREVIEW_UPDATE = 'AUCTION_MANAGER_THEME_PREVIEW_UPDATE';
const BACKGROUND_CONTRAST_RATIO = 2.23;
const DARKENING_AMOUNT = 0.15;

@Component({
    templateUrl: './theme-previewer.component.html',
    styleUrl: './theme-previewer.component.scss',
    imports: [
        ButtonModule,
        FontAwesomeModule,
        FormsModule,
        InputGroupModule,
        MessageModule,
        ColorPickerModule,
        InputTextModule,
    ]
})
export class ThemePreviewerComponent implements OnInit {
    private platformId = inject(PLATFORM_ID);
    private themeService = inject(ThemeService);

    public lightColorParam = input('');
    public darkColorParam = input('');
    public defaultLightColorParam = input('');
    public defaultDarkColorParam = input('');

    protected lightColor = '#0079BF';
    protected darkColor = '#004973';
    protected message = '';

    protected icons = {
        faCalendar: faCalendar,
        faTicket: faTicket,
        faSearch: faSearch,
        faShoppingBag: faShoppingBag,
        faRotateLeft: faRotateLeft,
    }

    ngOnInit() {
        this.themeService.setPreset('bfg');
        let useDefaultFontColors = true;
        if (this.lightColorParam() !== undefined && this.lightColorParam() !== '') {
            this.lightColor = this.lightColorParam();
            useDefaultFontColors = this.getDefaultLightColor() === this.lightColor;
        }
        if (this.darkColorParam() !== undefined && this.darkColorParam() !== '') {
            this.darkColor = this.darkColorParam();
        }
        this.updateTheme(useDefaultFontColors);
    }

    protected canRestoreLightColor = computed(() => {
        return this.lightColorParam() !== undefined && this.lightColorParam() !== ''
    });

    protected getDefaultLightColor() {
        if (this.defaultLightColorParam() !== undefined && this.defaultLightColorParam() !== '') {
            return this.defaultLightColorParam();
        } else {
            return '#0079BF';
        }
    }

    protected getDefaultDarkColor() {
        if (this.defaultDarkColorParam() !== undefined && this.defaultDarkColorParam() !== '') {
            return this.defaultDarkColorParam();
        } else {
            return '#004973';
        }
    }

    protected updateTheme(overrideLightFontColor = false) {
        this.darkColor = colord(this.lightColor).darken(DARKENING_AMOUNT).toHex();
        const darkFontColor = this.getBestFontColor(this.darkColor);
        const lightFontColor = overrideLightFontColor ? '#f8fafc' : this.getBestFontColor(this.lightColor);
        const errors = this.validateColors(this.darkColor, this.lightColor, darkFontColor, lightFontColor);
        this.message= '';
        errors.forEach(element => {
            this.message += element + '\n';
        });
        this.themeService.customizeTheme(this.darkColor, this.lightColor, darkFontColor, lightFontColor);
        if (isPlatformBrowser(this.platformId)) {
            const colorChangedMessage = {
                id: AUCTION_MANAGER_THEME_PREVIEW_UPDATE,
                lightColor: this.lightColor,
                darkColor: this.darkColor,
                lightFontColor: lightFontColor,
                darkFontColor: darkFontColor,
            };
            window.parent.postMessage(colorChangedMessage, environment.platformUrl + environment.themePreviewerIframeHostRoute);
            window.parent.postMessage(colorChangedMessage, environment.mediaUrl + environment.themePreviewerIframeHostRoute);
        }
    }

    protected restoreLightColor() {
        if (this.canRestoreLightColor()) {
            this.lightColor = this.lightColorParam();
            this.updateTheme();
        }
    }

    protected restoreDefaultColors() {
        this.lightColor = this.getDefaultLightColor();
        this.darkColor = this.getDefaultDarkColor();
        this.updateTheme(true);
    }

    private getBestFontColor(color: string) {
        return best('#f8fafc', '#020617', color);
    }

    private validateColors(darkColor: string, lightColor: string, darkFontColor: string, lightFontColor: string) {
        const errors = [];
        if (ratio(lightColor, '#fff') < BACKGROUND_CONTRAST_RATIO) {
            errors.push('The light color is too close to white. Consider a different light color, or users might have a hard time viewing some elements on the page.');
        } else if (ratio(lightColor, '#000') < BACKGROUND_CONTRAST_RATIO) {
            errors.push('For better results, consider using a brighter color.');
        }
        return errors;
    }
}
